import axios from "axios"
import { supabase } from "../supabase.ts"

const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session) {
      config.headers["user-id"] = session.user.id
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export type KnowledgeBaseStatus =
  | "in_progress"
  | "complete"
  | "error"
  | "refreshing_in_progress"

export interface KnowledgeBaseSource {
  type: "document" | "text" | "url"
  source_id: string
  filename?: string
  file_url?: string
  file_size?: number
  title?: string
  content_url?: string
  url?: string
}

export interface KnowledgeBase {
  knowledge_base_id: string
  knowledge_base_name: string
  status: KnowledgeBaseStatus
  knowledge_base_sources?: KnowledgeBaseSource[]
  enable_auto_refresh?: boolean
  last_refreshed_timestamp?: number
  max_chunk_size?: number
  min_chunk_size?: number
}

export interface KnowledgeBaseText {
  title: string
  text: string
}

/** Optional create fields; you can still pass `true` as the 5th arg for auto-refresh only (legacy). */
export type CreateKnowledgeBaseOptions = {
  enableAutoRefresh?: boolean
  maxChunkSize?: number
  minChunkSize?: number
}

function parseCreateExtras(
  extras?: boolean | CreateKnowledgeBaseOptions,
): CreateKnowledgeBaseOptions {
  if (typeof extras === "boolean") return { enableAutoRefresh: extras }
  return extras ?? {}
}

export const knowledgeBaseService = {
  list: async (): Promise<KnowledgeBase[]> => {
    const response = await api.get<KnowledgeBase[]>("/knowledge-bases")
    return response.data
  },

  get: async (knowledgeBaseId: string): Promise<KnowledgeBase> => {
    const response = await api.get<KnowledgeBase>(
      `/knowledge-bases/${knowledgeBaseId}`,
    )
    return response.data
  },

  create: async (
    name: string,
    texts?: KnowledgeBaseText[],
    files?: File[],
    urls?: string[],
    extras?: boolean | CreateKnowledgeBaseOptions,
  ): Promise<KnowledgeBase> => {
    const { enableAutoRefresh, maxChunkSize, minChunkSize } =
      parseCreateExtras(extras)

    const formData = new FormData()
    formData.append("name", name)

    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session) {
      formData.append("userId", session.user.id)
    }

    if (enableAutoRefresh !== undefined) {
      formData.append("enableAutoRefresh", String(enableAutoRefresh))
    }

    if (maxChunkSize !== undefined && Number.isFinite(maxChunkSize)) {
      formData.append("maxChunkSize", String(maxChunkSize))
    }
    if (minChunkSize !== undefined && Number.isFinite(minChunkSize)) {
      formData.append("minChunkSize", String(minChunkSize))
    }

    if (texts && texts.length > 0) {
      formData.append("texts", JSON.stringify(texts))
    }

    if (urls && urls.length > 0) {
      formData.append("urls", JSON.stringify(urls))
    }

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file)
      })
    }

    const response = await api.post<KnowledgeBase>("/knowledge-bases", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response.data
  },

  delete: async (knowledgeBaseId: string): Promise<void> => {
    await api.delete(`/knowledge-bases/${knowledgeBaseId}`)
  },

  addSources: async (
    knowledgeBaseId: string,
    texts?: KnowledgeBaseText[],
    files?: File[],
    urls?: string[],
  ): Promise<KnowledgeBase> => {
    const formData = new FormData()

    if (texts && texts.length > 0) {
      formData.append("texts", JSON.stringify(texts))
    }

    if (urls && urls.length > 0) {
      formData.append("urls", JSON.stringify(urls))
    }

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file)
      })
    }

    const response = await api.post<KnowledgeBase>(
      `/knowledge-bases/${knowledgeBaseId}/sources`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )

    return response.data
  },

  /** Same as `addSources` with URLs only (re-sync / bulk URL add). */
  addUrls: async (
    knowledgeBaseId: string,
    urls: string[],
  ): Promise<KnowledgeBase> => {
    return knowledgeBaseService.addSources(
      knowledgeBaseId,
      undefined,
      undefined,
      urls,
    )
  },

  removeSource: async (
    knowledgeBaseId: string,
    sourceId: string,
  ): Promise<KnowledgeBase> => {
    const response = await api.delete<KnowledgeBase>(
      `/knowledge-bases/${knowledgeBaseId}/sources/${sourceId}`,
    )
    return response.data
  },
}
